/**
 * El pie de la tienda.
 *
 * Es lo ultimo que ve alguien que bajo hasta el fondo sin encontrar lo que
 * buscaba, asi que arriba va lo unico que le sirve en ese momento: escribir.
 * Debajo, lo que antes eran tres renglones grises sin link —la direccion, el
 * horario, el Instagram— pasa a ser algo que se toca: la direccion abre el
 * mapa y el Instagram abre la cuenta. Y las categorias, para seguir mirando sin
 * volver a subir.
 *
 * Todo sale de la configuracion del negocio: lo que no esta cargado no se
 * dibuja, en vez de dejar un titulo con nada abajo.
 */
import type { ReactNode } from "react";
import { Clock, Instagram, MapPin, ShieldCheck } from "lucide-react";

import { TODO_EL_CATALOGO } from "./home";
import type { CategoriaPublica, NegocioPublico } from "./logica";
import { IconoWhatsapp, Marca } from "./piezas";

/** `@manzanamordida.arg` o un link entero: los dos llevan a la cuenta. */
function linkInstagram(crudo: string): string {
  const limpio = crudo.trim();
  if (/^https?:\/\//.test(limpio)) return limpio;
  return `https://www.instagram.com/${limpio.replace(/^@/, "")}`;
}

function linkMapa(lugar: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(lugar)}`;
}

/** «MANZANA MORDIDA» viene en mayusculas de la planilla; al pie va en letra comun. */
function nombreDelLocal(nombre: string | undefined): string {
  const n = nombre?.trim() || "Manzana Mordida";
  return n === n.toUpperCase() ? n.toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase()) : n;
}

export function Pie({
  negocio: n,
  linkGeneral,
  linkCanje = null,
  categorias,
  onCategoria,
  conBarra,
}: {
  negocio: NegocioPublico | null | undefined;
  linkGeneral: string | null;
  /** El mensaje para cotizar el equipo que se entrega como parte de pago. */
  linkCanje?: string | null;
  categorias: CategoriaPublica[];
  onCategoria: (slug: string) => void;
  /** La barra de la consulta tapa el fondo de la pagina: el pie se estira abajo de ella. */
  conBarra: boolean;
}): JSX.Element {
  const lugar = [n?.direccion?.trim(), n?.localidad?.trim()].filter(Boolean).join(", ");
  const garantias = [
    n?.garantia_sellado_meses ? `${n.garantia_sellado_meses} meses en sellados` : null,
    n?.garantia_usado_meses ? `${n.garantia_usado_meses} meses en usados` : null,
  ].filter(Boolean);
  const hayLocal = lugar !== "" || !!n?.horario || garantias.length > 0;
  const hayContacto = !!linkGeneral || !!n?.instagram;
  const nombre = nombreDelLocal(n?.nombre);

  return (
    <footer
      className={`mt-11 bg-[#0A0A0A] px-5 pt-9 text-white md:mt-20 md:pt-14 ${
        conBarra ? "pb-32 md:pb-12" : "pb-[max(2rem,env(safe-area-inset-bottom))] md:pb-12"
      }`}
    >
      <div className="mx-auto w-full max-w-[1200px] md:px-8 lg:px-10">
        {/* ── Lo que sirve al llegar al fondo ── */}
        <div className="md:grid md:grid-cols-[1fr_auto] md:items-end md:gap-x-12">
          <div>
            <h2 className="text-[23px] font-[700] leading-tight tracking-[-0.03em] md:text-[32px]">
              ¿No está el que buscás?
            </h2>
            <p className="mt-2.5 max-w-[46ch] text-sm leading-normal text-[#A8A8A8]">
              {n?.dias_a_pedido
                ? `Lo conseguimos en ${n.dias_a_pedido} días. `
                : "Lo conseguimos. "}
              Y te tomamos el tuyo como parte de pago.
            </p>
            <div className="mt-5 md:flex md:gap-3">
              {linkGeneral ? (
                <a
                  href={linkGeneral}
                  target="_blank"
                  rel="noreferrer"
                  className="flex h-[50px] items-center justify-center gap-2.5 rounded-md bg-white text-[15px] font-[700] text-[#0A0A0A] transition-colors hover:bg-[#E8E8E8] md:w-fit md:px-7"
                >
                  <IconoWhatsapp className="size-[18px]" />
                  Escribinos por WhatsApp
                </a>
              ) : null}
              {/* «Te tomamos el tuyo» necesitaba su propio mensaje: con el general
                el local tenia que preguntar que equipo y para que. */}
              {linkCanje ? (
                <a
                  href={linkCanje}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2.5 flex h-[50px] items-center justify-center rounded-md border border-white/40 text-[15px] font-semibold text-white transition-colors hover:border-white md:mt-0 md:w-fit md:px-7"
                >
                  Cotizá el tuyo
                </a>
              ) : null}
            </div>
          </div>

          {/* El logo en blanco: el mismo del encabezado, invertido. */}
          <div
            aria-hidden="true"
            className="hidden opacity-90 invert md:block [&_img]:!mx-0 [&_img]:!h-16"
          >
            <Marca logoUrl={n?.logo_url} />
          </div>
        </div>

        {/* ── Donde, como y que ── */}
        <div className="mt-9 grid grid-cols-1 gap-8 border-t border-white/15 pt-8 sm:grid-cols-2 md:mt-12 md:grid-cols-3 md:gap-12 md:pt-10">
          {hayLocal ? (
            <Columna titulo="El local">
              {lugar ? (
                <Renglon icono={<MapPin className="size-4" />} href={linkMapa(lugar)}>
                  {lugar}
                </Renglon>
              ) : null}
              {n?.horario ? (
                <Renglon icono={<Clock className="size-4" />}>{n.horario}</Renglon>
              ) : null}
              {garantias.length > 0 ? (
                <Renglon icono={<ShieldCheck className="size-4" />}>
                  Garantía: {garantias.join(" y ")}
                </Renglon>
              ) : null}
            </Columna>
          ) : null}

          {hayContacto ? (
            <Columna titulo="Contacto">
              {linkGeneral ? (
                <Renglon icono={<IconoWhatsapp className="size-4" />} href={linkGeneral}>
                  {n?.whatsapp?.trim() || "WhatsApp"}
                </Renglon>
              ) : null}
              {n?.instagram ? (
                <Renglon icono={<Instagram className="size-4" />} href={linkInstagram(n.instagram)}>
                  {n.instagram.trim()}
                </Renglon>
              ) : null}
            </Columna>
          ) : null}

          {categorias.length > 0 ? (
            <Columna titulo="Categorías">
              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 md:grid-cols-1">
                {categorias.map((c) => (
                  <li key={c.slug}>
                    <BotonCategoria onClick={() => onCategoria(c.slug)}>{c.nombre}</BotonCategoria>
                  </li>
                ))}
                <li>
                  <BotonCategoria onClick={() => onCategoria(TODO_EL_CATALOGO)}>
                    Todo el catálogo
                  </BotonCategoria>
                </li>
              </ul>
            </Columna>
          ) : null}
        </div>

        {/* ── Firma ── */}
        <div className="mt-10 flex flex-col gap-1.5 border-t border-white/15 pt-6 text-xs text-[#8A8A8A] md:mt-14 md:flex-row md:justify-between">
          <span>
            © {new Date().getFullYear()} {nombre}
          </span>
          <span>Precios del día.</span>
        </div>
      </div>
    </footer>
  );
}

function Columna({ titulo, children }: { titulo: string; children: ReactNode }): JSX.Element {
  return (
    <div>
      <h3 className="mb-3.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8A8A8A]">
        {titulo}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

/** Un dato con su icono; si lleva a algun lado, es un link. */
function Renglon({
  icono,
  href,
  children,
}: {
  icono: ReactNode;
  href?: string;
  children: ReactNode;
}): JSX.Element {
  const contenido = (
    <>
      <span className="mt-0.5 shrink-0 text-[#8A8A8A]" aria-hidden="true">
        {icono}
      </span>
      <span>{children}</span>
    </>
  );
  const clase = "flex items-start gap-2.5 text-sm leading-snug text-[#E0E0E0]";
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`${clase} underline-offset-4 transition-colors hover:text-white hover:underline`}
    >
      {contenido}
    </a>
  ) : (
    <div className={clase}>{contenido}</div>
  );
}

function BotonCategoria({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left text-sm text-[#E0E0E0] underline-offset-4 transition-colors hover:text-white hover:underline"
    >
      {children}
    </button>
  );
}
